import { defineStyle, defineStyleConfig } from '@chakra-ui/react'

// baseStyle
const baseStyle = defineStyle({
    textDecoration: 'none !important',
    fontWeight: 500,
    borderRadius: '8px',
    transition: 'background 0.3s, color 0.3s, border 0.3s',
    textTransform: 'uppercase',
    _disabled: {
        cursor: 'not-allowed',
    },
    _readOnly: {
        pointerEvents: 'none',
    },
})

// sizes
const sizes = {
    xs: defineStyle({
        fontSize: '0.625rem',
        lineHeight: '0.9375rem',
        height: '27px',
        padding: '6px 9px',
    }),
    sm: defineStyle({
        fontSize: '0.75rem',
        lineHeight: '0.9375rem',
        height: '33px',
        padding: '9px 14px',
    }),
    lg: defineStyle({
        fontSize: '0.875rem',
        lineHeight: '1.4375rem',
        height: '42px',
        padding: '10px 18px',
    }),
}

// variants
const variants = {
    'ai-platform': defineStyle({
        height: 'auto',
        padding: 3,
        gap: 2,
        flexDirection: 'column',
        alignItems: 'center',
        whiteSpace: 'normal',
        textTransform: 'none',
        color: 'captions.light',
        backgroundColor: 'white',
        borderWidth: '1px',
        borderColor: 'stroke.light',
        borderRadius: 'lg',
        _hover: {
            backgroundColor: 'beige.highlighted',
            borderColor: 'green.default',
        },
        _checked: {
            color: 'green.default',
            backgroundColor: 'beige.darkened_2',
            borderColor: 'green.default',
        },
        _focusVisible: {
            outline: '2px solid',
            outlineColor: 'neon.100',
            outlineOffset: '3px',
        },
    }),
    primary: defineStyle(({ theme }) => {
        const colors = theme.colors

        return {
            color: colors.green.default,
            backgroundColor: colors.neon['100'],
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: colors.neon['100'],
            _focus: {
                outlineWidth: '2px',
                outlineColor: colors.neon['50'],
                backgroundColor: colors.neon['100'],
                boxShadow: 'none',
            },
            _focusVisible: {
                outlineWidth: '2px',
                outlineColor: colors.neon['50'],
                backgroundColor: colors.neon['100'],
                boxShadow: 'none',
            },
            _hover: {
                backgroundColor: colors.neon['80'],
                borderColor: colors.neon['80'],
                transition: 'background 0.3s, color 0.3s, border 0.3s',
                _disabled: {
                    backgroundColor: colors.neon['50'],
                },
            },
            _disabled: {
                backgroundColor: colors.neon['50'],
                pointerEvents: 'none',
            },
            _active: {
                backgroundColor: colors.neon['130'],
                outline: 'none',
            },
        }
    }),
    secondary: defineStyle(({ theme }) => {
        const colors = theme.colors

        return {
            color: 'white',
            backgroundColor: colors.green.default,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: colors.green.default,
            _focus: {
                outlineWidth: '2px',
                outlineColor: colors.stroke.light,
                boxShadow: 'none',
            },
            _hover: {
                backgroundColor: colors.green.light,
                borderColor: colors.green.light,
                transition: 'background 0.3s, color 0.3s, border 0.3s',
            },
            _disabled: {
                backgroundColor: 'captions.disabled',
            },
            _active: {
                backgroundColor: colors.green.light,
                outline: 'none',
            },
        }
    }),
    tertiary: defineStyle(({ theme }) => {
        const colors = theme.colors

        return {
            color: colors.green.default,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: colors.stroke.light,
            _focus: {
                outlineWidth: '2px',
                outlineColor: colors.stroke.light,
                boxShadow: 'none',
                backgroundColor: colors.captions.dark,
                borderColor: 'transparent',
            },
            _hover: {
                backgroundColor: colors.green.transparent_3,
                borderColor: colors.stroke.light,
                transition: 'background 0.3s, color 0.3s, border 0.3s',
            },
            _disabled: {
                backgroundColor: 'colors.stroke.light',
                pointerEvents: 'none',
            },
            _active: {
                backgroundColor: colors.bg['green10'],
                outline: 'none',
            },
        }
    }),
    'tertiary-dark': defineStyle(({ theme }) => {
        const colors = theme.colors

        return {
            color: colors.beige.background,
            backgroundColor: colors.green.default,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: colors.stroke.dark,
            _focus: {
                outlineWidth: '3px',
                outlineColor: colors.stroke.dark,
                boxShadow: 'none',
                backgroundColor: colors.bg.beige10,
                borderColor: 'transparent',
            },
            _hover: {
                backgroundColor: colors.bg['beige6'],
                borderColor: colors.stroke.dark,
                transition: 'background 0.3s, color 0.3s, border 0.3s',
            },
            _disabled: {
                backgroundColor: 'colors.stroke.light',
                pointerEvents: 'none',
                color: colors.captions.dark,
            },
            _active: {
                backgroundColor: colors.bg.beige12,
                outline: 'none',
            },
        }
    }),
    ghost: defineStyle(({ theme }) => {
        const colors = theme.colors

        return {
            color: colors.green.default,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'transparent',
            _focus: {
                outline: 'none',
                boxShadow: 'none',
                backgroundColor: colors.bg['green6'],
                outlineWidth: '2px',
                outlineColor: colors.stroke.light,
            },
            _hover: {
                backgroundColor: colors.bg['green6'],
                transition: 'background 0.3s, color 0.3s, border 0.3s',
            },
            _disabled: {
                backgroundColor: 'captions.disabled',
            },
            _active: {
                outline: 'none',
                backgroundColor: colors.bg['green10'],
            },
        }
    }),
    'ghost-dark': defineStyle(({ theme }) => {
        const colors = theme.colors

        return {
            color: colors.beige.background,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'transparent',
            _focus: {
                outlineWidth: '3px',
                outlineColor: colors.stroke.dark,
                boxShadow: 'none',
                backgroundColor: colors.bg.beige10,
                borderColor: 'transparent',
            },
            _hover: {
                backgroundColor: colors.bg['beige10'],
                transition: 'background 0.3s, color 0.3s, border 0.3s',
            },
            _disabled: {
                backgroundColor: 'captions.disabled',
            },
            _active: {
                outline: 'none',
                backgroundColor: colors.captions.dark,
            },
        }
    }),
    white: defineStyle(({ theme }) => {
        const colors = theme.colors

        return {
            color: colors.green.default,
            bgColor: 'white',
            outline: 'none',
            transition: 'border 0.3s',
            _focus: {
                outlineWidth: '3px',
                outlineColor: colors.stroke.dark,
                borderColor: 'transparent',
            },
            _disabled: {
                bgColor: 'white !important',
            },
            _active: {
                bgColor: 'white',
            },
        }
    }),
    yellow: defineStyle(() => {
        return {
            color: 'green.default',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'yellow.100',
            bgColor: 'yellow.100',
        }
    }),
    'bank-details': defineStyle(({ theme }) => {
        const colors = theme.colors

        return {
            backgroundColor: colors.beige.darkened,
            color: colors.grey.grey2,
            height: '27px',
            fontSize: '0.75rem',
            _hover: {
                backgroundColor: colors.beige.darkened_shade,
            },
        }
    }),
    'term-picker': defineStyle(({ theme }) => {
        const colors = theme.colors

        return {
            rounded: '9px',
            borderWidth: '1px',
            bgColor: 'white',
            borderColor: colors.green.transparent_10,
            _hover: {
                borderColor: colors.green.default,
            },
            transition: 'border .25s ease',
            '&::before': {
                content: '""',
                w: '100%',
                h: '100%',
                rounded: '10px',
                transition: 'opacity .25s ease',
                borderWidth: '9px',
                border: '2px solid',
                borderColor: 'green.default',
                position: 'absolute',
                opacity: 0,
            },
            _checked: {
                '&::before': {
                    opacity: 1,
                },
            },
        }
    }),
}

export const Button = defineStyleConfig({
    baseStyle,
    sizes,
    variants,
    defaultProps: {
        variant: 'primary',
        colorScheme: 'primary',
        size: 'sm',
    },
})
